import {useEquipmentPerformances} from '@domain/kpis'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {ExpandedGroupIds, useExpandedGroups} from '@hconnect/common/hooks/useExpandGroups'
import {KpiVisibilityFlag, TimeZone} from '@hconnect/common/types'
import {ExpandCollapseButton} from '@hconnect/uikit/src/lib2'
import {ChevronRight} from '@mui/icons-material'
import {Typography, Box, Link, Theme, useMediaQuery} from '@mui/material'
import {
  mapEquipmentPerformances,
  EquipmentKpiCardsOverview,
  EquipmentKpiTableOverview
} from '@widgets/kpis'
import {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useTrackPlantStatusAnalyticsEvent, useTimeFrames} from '../hooks'
import {PlantStatusAnalyticsEventName} from '../types'
import {checkIfKpiPerformanceIsAvailable, filterEquipmentsWithKpiAssignments} from '../utils'

type PerformanceSummaryProps = {
  plantId: string
  timezone: TimeZone
  shiftSummaryHref: string
}

// Keeps plants with many KPIs from stretching the page; the rows scroll inside the card
const TABLE_MAX_HEIGHT = 'max(400px, calc(100vh - 300px))'

export const PlantEntryPointDailyKpiSummary: FC<PerformanceSummaryProps> = ({
  plantId,
  timezone,
  shiftSummaryHref
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {timeFrames} = useTimeFrames({timezone})
  const trackAnalyticsEvent = useTrackPlantStatusAnalyticsEvent(plantId)
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const {data: equipmentPerformances, isLoading} = useEquipmentPerformances({
    plantId,
    timeFrames,
    kpiVisibilityFlag: KpiVisibilityFlag.PlantPerformancePage
  })

  const equipmentPerformancesWithKpiAssignments = useMemo(
    () =>
      filterEquipmentsWithKpiAssignments(
        equipmentPerformances && mapEquipmentPerformances(equipmentPerformances, timezone)
      ),
    [equipmentPerformances, timezone]
  )
  const equipmentIndexes: string[] =
    equipmentPerformancesWithKpiAssignments?.map((_, index) => String(index)) ?? []
  const [expandedEquipments, toggleExpandedEquipments] = useExpandedGroups(
    equipmentIndexes,
    'daily-kpi-expanded-equipments'
  )
  const handleToggleEquipments = (id: ExpandedGroupIds) => {
    if (id === 'all') {
      trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserTogglesAllGeneralKpis, {})
    } else {
      trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserTogglesGeneralKpi, {
        equipmentId: equipmentPerformancesWithKpiAssignments?.[id].equipment.id
      })
    }
    toggleExpandedEquipments(id)
  }

  const isKpiPerformanceAvailable = checkIfKpiPerformanceIsAvailable(
    equipmentPerformancesWithKpiAssignments
  )
  return (
    <ContentBox
      mode="max100PercentOfParentHeight"
      title={
        <Typography component="span" variant="h3" display="flex" alignItems="center">
          {t('dailyKpiSummary.cardTitle')}
        </Typography>
      }
      isLoading={isLoading}
      afterTitle={
        isDesktop ? (
          <TitleActions
            plantId={plantId}
            expandedEquipments={expandedEquipments}
            toggleExpandedEquipments={handleToggleEquipments}
            equipmentIndexes={equipmentIndexes}
            isKpiPerformanceAvailable={isKpiPerformanceAvailable}
            shiftSummaryHref={shiftSummaryHref}
          />
        ) : null
      }
      bodyWithPadding
      data-test-id="daily-kpi-summary"
    >
      {equipmentPerformancesWithKpiAssignments?.length === 0 ? (
        <Box px={2} py={4}>
          <Typography>
            {`${t(
              'dailyKpiSummary.message.noKpisAreEnabled'
            )}. ${t('dailyKpiSummary.message.kpiEnableNote')}.`}
          </Typography>
        </Box>
      ) : (
        equipmentPerformancesWithKpiAssignments &&
        (isDesktop ? (
          <EquipmentKpiTableOverview
            data={equipmentPerformancesWithKpiAssignments}
            expandedEquipments={expandedEquipments}
            toggleExpandedEquipments={handleToggleEquipments}
            showKpiStatusIndicator
            timeFrames={timeFrames}
            maxHeight={TABLE_MAX_HEIGHT}
          />
        ) : (
          <EquipmentKpiCardsOverview
            data={equipmentPerformancesWithKpiAssignments}
            expandedEquipments={expandedEquipments}
            toggleExpandedEquipments={handleToggleEquipments}
            timeFrames={timeFrames}
            cardBoxSx={{boxShadow: 'none', px: 0, pb: 0, pt: 0}}
          />
        ))
      )}
    </ContentBox>
  )
}

type TitleActionsProps = {
  equipmentIndexes: string[]
  expandedEquipments: string[]
  toggleExpandedEquipments: (id: ExpandedGroupIds) => void
  isKpiPerformanceAvailable: boolean
  plantId: string
  shiftSummaryHref: string
}

const TitleActions = ({
  plantId,
  isKpiPerformanceAvailable,
  toggleExpandedEquipments,
  equipmentIndexes,
  expandedEquipments,
  shiftSummaryHref
}: TitleActionsProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const trackAnalyticsEvent = useTrackPlantStatusAnalyticsEvent(plantId)

  return (
    <Box display="flex" alignItems="center" gap={1.5}>
      {isKpiPerformanceAvailable && (
        <ExpandCollapseButton
          variant="text"
          expanded={expandedEquipments && expandedEquipments.length === equipmentIndexes.length}
          onClick={() => toggleExpandedEquipments('all')}
          data-test-id="performance-expand-all-button"
          expandLabel={t('dailyKpiSummary.action.expandAll')}
          collapseLabel={t('dailyKpiSummary.action.collapseAll')}
        />
      )}
      <Link
        href={shiftSummaryHref}
        data-test-id="performance-section-link-to-cockpit"
        sx={(theme) => ({
          textDecoration: 'none',
          '&:hover': {
            '& .card-chevron, .card-name': {
              color: theme.palette.primary.main
            }
          }
        })}
        onClick={() =>
          trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserGoesToShiftSummary, {})
        }
      >
        <ChevronRight />
      </Link>
    </Box>
  )
}
